import { MarvinWidgetDirective } from "../shared/interfaces";
import type {
    MarvinRfiFormReference,
    MarvinRfiFormSnapshot,
    MarvinRfiFormWidget,
} from "../shared/marvinWidgets";

interface MarkdownSegment {
    kind: "markdown";
    content: string;
}

interface WidgetSegment {
    kind: "widget";
    directive: MarvinWidgetDirective;
}

export type RenderSegment = MarkdownSegment | WidgetSegment;

const WIDGET_PREFIX = "::widget";
const FENCE_RE = /^(`{3,}|~{3,})(.*)?$/;

const MAX_RFI_REFERENCES = 8;
const MAX_RFI_SNAPSHOTS = 4;

function isRfiRef(value: unknown): value is { ref: number } {
    return (
        !!value &&
        typeof value === "object" &&
        Number.isInteger((value as Record<string, unknown>).ref)
    );
}

/** The entries of an RFI card's list that are well formed, capped; a malformed entry is dropped. */
function rfiEntries<T extends { ref: number }>(
    value: unknown,
    valid: (entry: T) => boolean,
    cap: number,
): T[] {
    if (!Array.isArray(value)) return [];
    return value.filter((entry): entry is T => isRfiRef(entry) && valid(entry as T)).slice(0, cap);
}

/** An RFI card with its lists cleaned (malformed entries dropped, lists capped); null when it
 *  lacks a form id, subject or question. */
function cleanRfiForm(directive: Record<string, unknown>): MarvinRfiFormWidget | null {
    const { form_id: formId, subject, question, background } = directive;
    if (typeof formId !== "string" || !formId) return null;
    if (typeof subject !== "string" || typeof question !== "string") return null;
    return {
        type: "rfi_form",
        form_id: formId,
        subject,
        question,
        ...(typeof background === "string" ? { background } : {}),
        drawing_references: rfiEntries<MarvinRfiFormReference>(
            directive.drawing_references,
            (entry) => typeof entry.label === "string",
            MAX_RFI_REFERENCES,
        ),
        spec_references: rfiEntries<MarvinRfiFormReference>(
            directive.spec_references,
            (entry) => typeof entry.label === "string",
            MAX_RFI_REFERENCES,
        ),
        snapshots: rfiEntries<MarvinRfiFormSnapshot>(
            directive.snapshots,
            (entry) => entry.caption === undefined || typeof entry.caption === "string",
            MAX_RFI_SNAPSHOTS,
        ),
    };
}

function isSupportedWidgetDirective(value: unknown): value is MarvinWidgetDirective {
    if (!value || typeof value !== "object") {
        return false;
    }

    const directive = value as Record<string, unknown>;
    if (directive.type === "detail_references" || directive.type === "drawing_view") {
        return (
            typeof directive.reference_index === "number" &&
            Number.isInteger(directive.reference_index)
        );
    }
    if (directive.type === "dig_deeper") {
        return (
            typeof directive.request === "string" &&
            directive.request.trim().length > 0 &&
            Array.isArray(directive.checks) &&
            directive.checks.every((check) => typeof check === "string") &&
            (directive.item_count === undefined ||
                (typeof directive.item_count === "number" &&
                    typeof directive.item_noun === "string"))
        );
    }
    if (directive.type === "project_controls") {
        const timeRange = directive.timeRange;
        return (
            typeof directive.title === "string" &&
            typeof directive.summary === "string" &&
            typeof directive.intent === "string" &&
            Array.isArray(directive.kinds) &&
            directive.kinds.every((kind) => typeof kind === "string") &&
            timeRange !== null &&
            typeof timeRange === "object" &&
            typeof (timeRange as Record<string, unknown>).from === "string" &&
            typeof (timeRange as Record<string, unknown>).to === "string" &&
            Array.isArray(directive.metrics) &&
            Array.isArray(directive.series) &&
            Array.isArray(directive.breakdowns) &&
            directive.breakdowns.every(
                (bd) =>
                    bd !== null &&
                    typeof bd === "object" &&
                    typeof (bd as Record<string, unknown>).dimension === "string",
            ) &&
            Array.isArray(directive.rows) &&
            directive.rows.every(
                (row) =>
                    row !== null &&
                    typeof row === "object" &&
                    typeof (row as Record<string, unknown>).kind === "string",
            )
        );
    }

    return false;
}

function parseWidgetDirective(line: string): MarvinWidgetDirective | null {
    if (!line.startsWith(WIDGET_PREFIX)) {
        return null;
    }

    let payload = line.slice(WIDGET_PREFIX.length).trim();
    // The artifact directive is emitted with a trailing "::" delimiter
    // (e.g. ::widget{"id":"...","type":"png"}::); tolerate it here.
    if (payload.endsWith("::")) {
        payload = payload.slice(0, -2).trim();
    }
    if (!payload.startsWith("{") || !payload.endsWith("}")) {
        return null;
    }

    try {
        const parsed = JSON.parse(payload);
        if (parsed?.type === "rfi_form") {
            const form = cleanRfiForm(parsed);
            if (form) return form;
        } else if (isSupportedWidgetDirective(parsed)) {
            return parsed;
        }
        console.warn("Unsupported widget directive:", parsed);
        return null;
    } catch (e) {
        console.warn("Failed to parse widget directive JSON:", payload, e);
        return null;
    }
}

export function splitMarkdownByWidgets(content: string): RenderSegment[] {
    if (!content) {
        return [];
    }

    const lines = content.split("\n");
    const segments: RenderSegment[] = [];
    const markdownBuffer: string[] = [];
    let openFence: { marker: "`" | "~"; length: number } | null = null;

    const flushMarkdown = () => {
        if (markdownBuffer.length === 0) {
            return;
        }
        segments.push({ kind: "markdown", content: markdownBuffer.join("\n") });
        markdownBuffer.length = 0;
    };

    for (const line of lines) {
        const trimmed = line.trim();
        const fenceMatch = trimmed.match(FENCE_RE);
        if (fenceMatch) {
            const marker = fenceMatch[1][0] as "`" | "~";
            const length = fenceMatch[1].length;
            if (!openFence) {
                openFence = { marker, length };
            } else if (openFence.marker === marker && length >= openFence.length) {
                openFence = null;
            }
            markdownBuffer.push(line);
            continue;
        }

        if (!openFence) {
            const directive = parseWidgetDirective(trimmed);
            if (directive) {
                flushMarkdown();
                segments.push({ kind: "widget", directive });
                continue;
            }

            if (trimmed.startsWith(WIDGET_PREFIX)) {
                flushMarkdown();
                continue;
            }
        }

        markdownBuffer.push(line);
    }

    flushMarkdown();
    return segments;
}
